import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Star } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  name: "",
  email: "",
  rating: 5,
  market: "",
  review: "",
};

export default function ReviewsPage() {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        email: formData.email || undefined,
        market: formData.market || undefined,
      };
      await axios.post(`${API_URL}/api/reviews`, payload);
      setSubmitted(true);
      setFormData(initialFormData);
      toast.success("Review submitted for approval.");
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Too many review attempts. Please try again later.");
      } else {
        toast.error("Unable to submit your review. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fff7d6]" data-testid="reviews-page">
      <header className="bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center" data-testid="reviews-logo-link">
              <BrandLogo />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-stone-600 hover:text-[#073D2D] transition-colors"
              data-testid="reviews-back-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#073D2D] mb-3">
              Customer Review
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1C1917] mb-4">
              Share your market sourcing experience
            </h1>
          </div>

          {submitted && (
            <div
              className="mb-6 flex items-start gap-3 rounded-lg border border-[#073D2D]/20 bg-white p-4 text-[#073D2D]"
              data-testid="review-success-message"
            >
              <CheckCircle className="w-5 h-5 mt-0.5" />
              <p className="text-sm">
                Thanks for the review. It has been submitted for moderation.
              </p>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="space-y-6 rounded-lg border border-stone-200 bg-white p-5 md:p-7 shadow-sm"
            data-testid="review-form"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="review-name">Name *</Label>
                <Input
                  id="review-name"
                  value={formData.name}
                  onChange={(event) => updateField("name", event.target.value)}
                  required
                  minLength={2}
                  maxLength={120}
                  placeholder="Your name"
                  data-testid="review-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="review-email">Email</Label>
                <Input
                  id="review-email"
                  type="email"
                  value={formData.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  placeholder="name@example.com"
                  data-testid="review-email-input"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Rating *</Label>
              <div className="flex gap-2" data-testid="review-rating-control">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => updateField("rating", rating)}
                    className="h-10 w-10 rounded-full border border-stone-200 bg-white text-[#073D2D] transition hover:border-[#073D2D] focus:outline-none focus:ring-2 focus:ring-[#073D2D]/30"
                    aria-label={`${rating} star${rating === 1 ? "" : "s"}`}
                    data-testid={`review-rating-${rating}`}
                  >
                    <Star
                      className={`mx-auto h-5 w-5 ${
                        rating <= formData.rating ? "fill-[#e7c139] text-[#e7c139]" : "text-stone-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="review-market">Market or service</Label>
              <Input
                id="review-market"
                value={formData.market}
                onChange={(event) => updateField("market", event.target.value)}
                maxLength={120}
                placeholder="Balogun Market, procurement support, etc."
                data-testid="review-market-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="review-text">Review *</Label>
              <Textarea
                id="review-text"
                value={formData.review}
                onChange={(event) => updateField("review", event.target.value)}
                required
                minLength={10}
                maxLength={1200}
                rows={7}
                placeholder="Tell us what worked well and what buyers should know."
                data-testid="review-text-input"
              />
              <p className="text-xs text-stone-500">{formData.review.length}/1200</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#073D2D] hover:bg-[#052f23] text-white rounded-full py-6"
              data-testid="review-submit-btn"
            >
              {loading ? "Submitting..." : "Submit Review"}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
